import SiteShell from "../components/SiteShell";
import DiagramFlow from "../components/DiagramFlow";

const retailCards = [
  {
    title: "Faster Customer Conversion",
    text: "Salaried walk-ins can move from intent to approved personal loan in under 3 minutes.",
    image: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg",
  },
  {
    title: "Lower Operational Load",
    text: "The self-service flow reduces branch dependency for routine personal loan onboarding.",
    image: "https://images.pexels.com/photos/5473298/pexels-photo-5473298.jpeg",
  },
  {
    title: "Consistent Decision Quality",
    text: "Standardized digital checks improve reliability and reduce manual processing errors.",
    image: "https://images.pexels.com/photos/7567434/pexels-photo-7567434.jpeg",
  },
];

function Retail() {
  return (
    <SiteShell
      route="retail"
      heroHidden={true}
      highlightsHidden={true}
    >
      <section className="relative overflow-hidden bg-[#061a2e] pb-12 pt-24 text-white">
        <div className="absolute inset-0 z-0">
          <div className="absolute -left-[10%] top-0 h-full w-[40%] bg-gradient-to-r from-sky-400/20 to-transparent blur-3xl" />
          <div className="absolute -right-[10%] bottom-0 h-full w-[40%] bg-gradient-to-l from-[#df1f2d]/10 to-transparent blur-3xl" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1280px] px-6 md:px-10 lg:grid-cols-2 grid items-center gap-12">
          <div>
            <span className="inline-flex rounded-full bg-white/10 px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.25em] text-sky-300 ring-1 ring-white/20 backdrop-blur-md">
              Business Impact
            </span>
            <h1 className="mt-8 text-4xl font-extrabold leading-tight tracking-tight md:text-6xl">
              Drive Growth with <br />Instant Approvals
            </h1>
            <p className="mt-8 max-w-xl text-lg text-sky-100/80 leading-relaxed">
              Our kiosk model is designed to maximize conversion and operational efficiency by putting the lending journey directly in the hands of the customer.
            </p>
            {/* <div className="mt-12 flex flex-wrap gap-4 text-[11px] font-black uppercase tracking-widest">
              <a href="#/services" className="rounded-full bg-white px-10 py-5 text-[#0a2c4e] shadow-2xl transition-all hover:-translate-y-1">Deployment Kits →</a>
              <a href="#/company" className="rounded-full border border-white/20 bg-white/5 px-10 py-5 text-white backdrop-blur-md transition-all hover:bg-white/10">Partner Portal</a>
            </div> */}
          </div>
          <div className="relative hidden lg:block">
            <div className="absolute -inset-4 bg-white/5 blur-2xl rounded-3xl" />
            <img src="https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg" alt="Retail impact" className="relative rounded-3xl border border-white/10 shadow-2xl" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-6 pb-8 md:px-10">
        <div className="grid gap-4 rounded-3xl border border-slate-200 bg-white/80 p-4 shadow-[0_20px_50px_-12px_rgba(10,44,79,0.12)] backdrop-blur-2xl md:grid-cols-4 lg:p-6">
          {[
            { label: "Processing Speed", value: "Real-time" },
            { label: "Target Audience", value: "Salaried" },
            { label: "Security Level", value: "Level 4" },
            { label: "Uptime Guaranteed", value: "99.99%" },
          ].map((item) => (
            <article
              key={item.label}
              className="group relative rounded-2xl bg-slate-50/50 p-6 transition-all hover:bg-white hover:shadow-xl"
            >
              <div className="absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-r-full bg-[#e37921] opacity-0 transition-opacity group-hover:opacity-100" />
              <p className="text-[15px] font-black uppercase tracking-[0.2em] text-[#e37921]/80">
                {item.label}
              </p>
              <h3 className="mt-2 text-xl font-extrabold tracking-tight text-[#0a2c4f]">
                {item.value}
              </h3>
            </article>
          ))}
        </div>
      </section>

      <DiagramFlow
        title="Value and Impact Metrics"
        subtitle="Experience measurable improvements in customer throughput and branch operational costs from day one."
        steps={[
          { title: "Direct Entry", text: "Salaried users initiate journeys without manual intervention." },
          { title: "Instant Qualification", text: "Automated checks instantly filter and qualify high-intent applicants." },
          { title: "High Conversion", text: "The sub-3-minute speed significantly drives higher completion rates." },
          { title: "Brand Trust", text: "A premium, secure interface builds lasting customer confidence." },
        ]}
      />

      <section className="mx-auto max-w-[1280px] px-6 py-20 md:px-10">
        <h2 className="text-3xl font-extrabold tracking-tight text-[#0a2c4f] md:text-4xl">Measurable Benefits</h2>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {retailCards.map((card) => (
            <article
              key={card.title}
              className="group overflow-hidden rounded-[32px] border border-slate-100 bg-white p-2 shadow-xl transition-all duration-500 hover:-translate-y-2"
            >
              <div className="relative h-56 overflow-hidden rounded-[24px]">
                <img src={card.image} alt={card.title} className="h-full w-full object-cover transition duration-700 group-hover:scale-110" />
              </div>
              <div className="p-8">
                <h3 className="text-xl font-bold tracking-tight text-[#0a2c4f]">{card.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-500">{card.text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-6 pb-24 md:px-10">
        <div className="grid gap-12 overflow-hidden rounded-[40px] border border-slate-200 bg-white shadow-2xl lg:grid-cols-2">
          <div className="relative bg-[#0a2c4e]/5 p-8 lg:p-12">
            <img
              src="/finnCube2.jpeg"
              alt="Customer using loan kiosk"
              className="relative h-full w-full rounded-2xl object-cover shadow-lg"
            />
          </div>
          <div className="p-10 md:p-16 flex flex-col justify-center">
            <p className="text-[15px] font-black uppercase tracking-[0.25em] text-[#e37921]">
              Experience
            </p>
            <h2 className="mt-6 text-3xl font-extrabold tracking-tight text-[#0a2c4f]">
              Intuitive, Visual, Fast.
            </h2>
            <p className="mt-8 text-base leading-relaxed text-slate-500">
              The user interface is engineered to eliminate friction. Every step is clearly articulated, reducing hesitation and maximizing focus on the personal loan completion.
            </p>
            <div className="mt-10 space-y-4">
              {[
                "Visual progress visualization",
                "Context-aware document guidance",
                "Real-time offer eligibility display"
              ].map((item) => (
                <div key={item} className="flex items-center gap-4 rounded-2xl bg-slate-50 p-4 transition-all hover:bg-white hover:shadow-md">
                  <div className="h-2 w-2 rounded-full bg-[#e37921]" />
                  <p className="text-sm font-bold text-[#17385d]">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </SiteShell >
  );
}

export default Retail;
